<template>
	<div id="configurator"> 
		<Presets 
			:class="{active : show_presets}"
			@start-configuration="show_presets = false"
		/>
		
		<Recap 
			v-if="current_preset"
			:class="{ active : show_recap}"
			:sample="image_sample"
			@back="show_recap = false"
		/>
		
		<div class="ruler flex" :class="{active : !show_presets}">
			<div class="segment" 
				v-for="n in Math.ceil(window_width/(800/scale))" 
				:style="{width : (unit == 'cm' ? (scale*50) : (scale*50.8))+'px'}"
			>
				<span class="small-font">{{ (unit == 'cm' ? 50*(n-1) : 20*(n-1))+unit }}</span>
			</div>
		</div>
		
		<div v-if="current_preset" class="svg-container" :style="{backgroundColor: background_color}">
			<!-- SVG usato solo per la foto per il recap -->
			<svg id="svg-sample"
				:width="(parseFloat(current_preset.width)+gap*2)*scale" 
				:height="(parseFloat(current_preset.height)+gap*2)*scale" 
				ref="sample"
			>
				<g id="patternsample" :style="{stroke : background_color}" >
					<template v-for="group in current_preset.groups" >
						<g v-for="tile in group.tiles" :class="'rotation-'+group.rotation">
							<template v-for="(shape,i) in group.tile.data">
								<path 
									:d="getTilePath(shape, tile, scale)"
									:fill="group.colors[i]"
								/>
								
								<image 
									v-if="group.types[i] == 'gloss'"
									:x="(shape[0][0] + tile[0])*scale" 
									:y="(shape[0][1] + tile[1])*scale" 
									:height="(shape[3][1] - shape[0][1])*scale"
									:href="require('../assets/gloss.png')" 
								/>
							</template>
						</g>
					</template >
				</g>
				
				<template v-for="x in 3" :style="{backgroundColor: background_color}">
					<use 
						v-for="y in 3" 
						xlink:href="#patternsample" 
						:x="((parseFloat(current_preset.width)+gap)*scale)*(x-2)" 
						:y="((parseFloat(current_preset.height)+gap)*scale)*(y-2)"
					/>
				</template>
			</svg>
				
			<!-- SVG di background -->
			<svg id="svg-bkg">
				<g id="pattern" :style="{stroke : background_color}" >
					<template v-for="group in current_preset.groups" >
						<g v-for="tile in group.tiles" :class="'rotation-'+group.rotation">
							<template v-for="(shape,i) in group.tile.data">
								<path 
									:d="getTilePath(shape, tile, scale)"
									:fill="group.colors[i]"
								/>
								
								<image 
									v-if="group.types[i] == 'gloss'"
									:x="(shape[0][0] + tile[0])*scale" 
									:y="(shape[0][1] + tile[1])*scale" 
									:height="(shape[3][1] - shape[0][1])*scale"
									:href="require('../assets/gloss.png')" 
								/>
							</template>
						</g>
					</template >
				</g>
				
				<template v-for="x in tile_limits[0]+2">
					<use 
						v-for="y in tile_limits[1]+2" 
						xlink:href="#pattern" 
						:x="((parseFloat(current_preset.width)+gap)*scale)*(x-2) + 30" 
						:y="((parseFloat(current_preset.height)+gap)*scale)*(y-2)"
					/>
				</template>
			</svg>
		</div>
	
		<div class="actions flex space">
			<Button secondary @click="show_presets = true">
				<span class="material-symbols-rounded">arrow_left_alt</span>
			</Button>
			
			<Button secondary @click="showRecap">
				<span class="padding-left-05x">{{ getLocale('proceed') }}</span>
				<span class="material-symbols-rounded">arrow_right_alt</span>
			</Button>
		</div>
		
		<div v-if="current_preset"class="info">
			<section>
				<div class="section-header medium-font flex space v-center">
					<div>{{ getLocale('settings') }} {{hovered}}</div>
					<ToggleIcon v-model="settings_toggle" />
				</div>
				
				<slide-up-down :active="settings_toggle" :duration="300">
					<div class="section-content flex column gap-s">
						<div class="small-font uppercase">{{ getLocale('laying_pattern') }}</div>
						<div class="presets flex gap-s wrap">
							<div  v-for="preset in patterns" class="preset" :class="{current : current_preset != null && preset.ID == current_preset.ID}" >
								<svg 
									@click="setPreset(preset)"
									:viewBox="'0 0 '+base_tile[0]*scale+' '+base_tile[1]*scale"
									@mouseenter="hovered = preset.ID" 
									@mouseleave="hovered = null"
								>
									<g :id="'patternpresetm'+preset.ID" class="pattern" >
										<template v-for="group in preset.groups" >
											<g v-for="tile in group.tiles" :class="'rotation-'+group.rotation">
												<path 
													v-for="(shape,i) in group.tile.data"
													:d="getTilePath(shape, tile, scale)" 
													:fill="preset.ID == hovered ? group.colors[i] : 'white'"
													stroke="black"
												/>
											</g>
										</template>
									</g>
									
									<template  v-for="x in 3">
										<use 
											v-for="y in 3" 
											:xlink:href="'#patternpresetm'+preset.ID" 
											:x="((parseFloat(preset.width)+gap)*scale)*(x-2)" 
											:y="((parseFloat(preset.height)+gap)*scale)*(y-2)"
										/>
									</template >
								</svg>
							</div>
						</div>
						
						<div class="flex  v-center gap-s">
							<div class="small-font uppercase">{{ getLocale('material') }}</div>
							<div>{{ getLocale('gres') }}</div>
						</div>
						
						<div class="flex  v-start gap-s">
							<div class="small-font uppercase">{{ getLocale('dimensions') }}</div>
							<div class="tiles flex wrap gap-s">
								<div v-for="tile in findAllTiles(current_preset)" class="tile index-tile">
									<svg 
										:width="parseFloat(tile.width) * scale"
										:height="parseFloat(tile.height) * scale"
									>
										<path 
											v-for="shape in tile.data"
											:d="getTilePath(shape, [0,0], scale)" 
											fill="white"
											stroke="black"
										/>
									</svg>
									<div class="text">{{ tile.info.size.title.split(' - ')[unit == 'cm' ? 0 : 1] }}</div>
								</div>
							</div>
						</div>
					</div>
				</slide-up-down>
			</section>
			
			<section>
				<div class="section-header medium-font">{{ getLocale('tiles_color') }}</div>
				
				<div class="rows">
					<div v-for="(group, i) in current_preset.groups" class="row">
						<div class="row-header flex space v-center">
							<div class="flex v-center">
								<div class="index small-font">{{ i+1 }}</div>
								
								<div class="flex gap-xs v-center">
									<svg class="color" :style="{backgroundColor: group.colors[0]}">
										<g v-if="group.tile.data.length > 1">
											<path 
												v-for="(shape,j) in group.tile.data"
												:d="getPieSlice(j, group.tile.data.length)"
												:fill="group.colors[j]"
											/>
										</g>
									</svg>
									
									<div class="name">
										<span v-for="(shape,j) in group.tile.data">
											{{ findColor(group.tile.info.colors, group.colors[j]).title }}
										</span>
									</div>
								</div>
							</div>
							
							<ToggleIcon v-model="group.toggled"/>
						</div>
						
						<slide-up-down :active="group.toggled" :duration="300">
							<div class="wrapper" :class="{multiple : group.tile.data.length > 1}">
								
								<div v-if="group.tile.data.length > 1" class="tabs flex small-font">
									<div class="tab" :class="{current : mode == 'default'}" @click="mode='default'">{{ getLocale('default') }}</div>
									<div class="tab" :class="{current : mode == 'custom'}" @click="mode='custom'">{{ getLocale('custom') }}</div>
								</div>
								
								<div v-if="group.tile.data.length == 1 || mode == 'custom'" class="rows custom">
									<div 
										v-for="(shape,j) in group.tile.data" 
										class="row-content flex space gap-xs"
									>
										<div class="flex gap-xs wrap">
											<div v-for="color in group.tile.info.colors" 
												class="color" 
												:class="{current : color.rgb == group.colors[j]}"
												@click="group.colors[j] = color.rgb"
												:style="{backgroundColor: color.rgb}"
											/>
										</div>
										
										<Switch 
											v-model="group.types[j]"
											:options="[
												{ text: 'MATT', value: 'matt' }, 
												{ text: 'GLOSSY', value: 'gloss' }, 
											]"
										/>
									</div>
									
									<div v-if="mode == 'custom' && isCustom(group)" class="row-content small-font min-availability">
										{{ getLocale('minimum_availability') }}
									</div>
								</div>
								
								<div v-else class="rows default">
									<div class="row-content flex space gap-xs">
										<template v-for="preset in group.presets">
											<svg 
												class="color" 
												:class="{current : 	
													preset.colors[0] == group.colors[0] &&
													preset.colors[1] == group.colors[1] 
												}"
												:style="{backgroundColor: preset.colors[0]}"
												@click="setColorPreset(group, preset, false)"
											>
												<g v-if="group.tile.data.length > 1">
													<path 
														v-for="n in 2"
														:d="getPieSlice(n-1, 2)"
														:fill="preset.colors[n-1]"
													/>
												</g>
											</svg>
											
											<svg 
												class="color" 
												:class="{current : 	
													preset.colors[1] == group.colors[0] &&
													preset.colors[0] == group.colors[1] 
												}"
												:style="{backgroundColor: preset.colors[1]}"
												@click="setColorPreset(group, preset, true)"
											>
												<g v-if="group.tile.data.length > 1">
													<path 
														v-for="n in 2"
														:d="getPieSlice(n-1, 2)"
														:fill="preset.colors[2-n]"
													/>
												</g>
											</svg>
										</template>
									</div>
								</div>
							</div>
						</slide-up-down>
					</div>
				</div>
			</section>
			
			<section>
				<div class="section-header medium-font">{{ getLocale('background_color') }}</div>
				
				<div class="row">
					<div class="row-header flex space v-center">
						<div class="flex v-center">
							<div class="index small-font">&nbsp;</div>
							
							<div class="flex gap-xs v-center">
								<div class="color" :style="{backgroundColor: background_color}"></div>
								
								<div class="name">{{ findColor(bkg_colors, background_color).name }}</div>
							</div>
						</div>
						
						<ToggleIcon v-model="background_toggle"/>
					</div>
					
					<slide-up-down :active="background_toggle" :duration="300">
						<div class="row-content flex wrap gap-xs">
							<div class="stucco" v-for="color in bkg_colors">
								<div
									class="color" 
									:class="{current : color.rgb == background_color}"
									@click="setBackground(color.rgb)"
									:style="{backgroundColor: color.rgb}"
								/>
							</div>
						</div>
					</slide-up-down>
				</div>
			</section>
		</div>
	
	</div>
</template>

<script>
	import { commonMixin } from '@/mixins/common.js';
	
	import { mapState, mapMutations } from 'vuex'
	import Recap from '@/components/Recap.vue'
	import Presets from '@/components/Presets.vue'
	import Button from '@/components/Button.vue'
	import InputButton from '@/components/InputButton.vue'
	import ToggleIcon from '@/components/ToggleIcon.vue'
	import Switch from '@/components/Switch.vue'
	import SlideUpDown from 'vue-slide-up-down'
	
	import { svgAsPngUri } from 'save-svg-as-png';
	
	export default {
		name: 'Configurator',
		components: { InputButton, ToggleIcon, Presets, Recap, Button, Switch, SlideUpDown },
		mixins: [ commonMixin ],
		data: function () { return {
			window_width : window.innerWidth,
			window_height : window.innerHeight,
			show_presets : true,
			show_recap : false,
			settings_toggle : false,
			background_toggle : false,
			scale : 8,
			mode : 'default',
			hovered : null,
			image_sample : ''
		}},
		computed: {
			...mapState([ 'patterns', 'current_preset', 'colors', 'bkg_colors', 'background_color', 'base_tile', 'gap', 'creation_data', 'unit' ]),
			tile_limits(){
				let count_x = Math.ceil(this.window_width/(parseFloat(this.current_preset.width)*this.scale));
				let count_y = Math.ceil(this.window_height/(parseFloat(this.current_preset.height)*this.scale));
				return [count_x, count_y];
			}
		},
		mounted(){
			window.addEventListener( 'resize', this.onWindowResize );
			this.onWindowResize();
			
			if(this.creation_data != null){
				this.show_presets = false;
			}
		},
		methods: {
			...mapMutations([ 'setPreset', 'setBackground' ]),
			
			onWindowResize(){
				this.window_width = window.innerWidth;
				this.window_height = window.innerHeight;
			},
			
			findAllTiles(pattern){
				let tiles = [];
				pattern.groups.forEach(g => {
					if( tiles.findIndex(t => t.ID == g.tile.ID) == -1 ){
						tiles.push(g.tile);
					}
				});
				return tiles;
			},
			
			setColorPreset(group, preset, reverse){
				group.colors[0] = preset.colors[reverse ? 1 : 0];
				group.colors[1] = preset.colors[reverse ? 0 : 1];
				group.types[0] = preset.types[reverse ? 1 : 0];
				group.types[1] = preset.types[reverse ? 0 : 1];
			},
			
			showRecap(){
				svgAsPngUri(this.$refs.sample, { scale: 1 }).then(uri => {
					this.image_sample = uri;
				});
				this.show_recap = true;
			}
		}
	}
	
</script>

<style lang="scss">
	#configurator{
		position: relative;
		height: calc(100vh - 90px);
		
		.ruler{
			position: absolute;
			bottom: 100%;
			left: 30px;
			width: calc(100% - 30px);  
			opacity: 0;
			transition: 0.3s;
			transition-timing-function: cubic-bezier(0,1.15,0.15,1);
			
			&.active{
				opacity: 1;
				transition-timing-function: cubic-bezier(1,-0.15,.85,0);
			}
			
			.segment{
				height: 8px;
				border-left: 2px solid black;
				flex-shrink: 0;
				
				span{
					position: absolute;
					bottom: calc(100% + 2px);
					transform: translateX(-50%);
				}
			}
		
			@media (max-width: 479px) {
				display: none;
			}
		}
		
		.svg-container{
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background: grey;
			
			#svg-bkg{
				position: absolute;
				left: 0;
				top: 0;
				width: 100%;
				height: 100%;
				background: lightgrey;
				stroke-width: 1;
			}
			
			#svg-sample{
				position: absolute;
				left: 0;
				top: 0;
				stroke-width: 1;
			}
		}
		
		.actions{
			position: absolute;
			left: 20px;
			top: 20px;
			width: calc(100% - 40px);
		}
		
		.info{
			position: absolute;
			left: 20px;
			top: 80px;
			width: 470px;
			max-height: calc(100vh - 190px);
			overflow-y: auto;
			background: var(--white);
			border-radius: 10px;
			overflow-y: auto;
			
			@media (max-width: 499px) {
				width: calc(100vw - 40px);
			}
			
			section{
				border-bottom: 1px solid black;
				
				&:last-child{
					border-bottom: none;
				}
				
				.section-header,
				.section-content{
					padding: 16px;
					
					.preset{
						width: 64px;
						height: 64px;
						border: 1px solid black;
						cursor: pointer;
						
						&.current{
							cursor: default;
							pointer-events: none;
							outline: 2px solid black;
						}
						
						svg{
							shape-rendering: auto;
						}
					}
					
					.tile{
						.image{
							width: 50px;
							height: 50px;
							border: 1px solid black;
							margin: 0 auto;
						}
						.text{
							margin-top: 5px;
						}
					}
					.index-tile svg{
						stroke-width: 2;
					}
				}
				
				.row{
					border-bottom: 1px solid #00000033;	
					
					&:last-child{
						border-bottom: none;
					}
					
					.row-header{
						padding: 10px 0;
						padding-right: 16px;
						
						.index{
							width: 60px;
							text-align: center;
						}
						.name span:not(:first-child):before{
							content: '+';
							padding: 0 10px;
						}
					}
					
					.tabs{
						.tab{
							text-transform: uppercase;
							padding: 10px;
							border-top-left-radius: 10px;
							border-top-right-radius: 10px;
							cursor: pointer;
							
							&.current{
								background: var(--white);
								cursor: default;
							}
						}
					}
					.wrapper{
						margin-top: 10px;
						
						&.multiple{
							background: #E4E2DE;
							padding: 10px;
							
							.row-content{
								background: white;
								
								&:first-child{
									border-top-left-radius: 10px;
									border-top-right-radius: 10px;
								}
								&:last-child{
									border-bottom-left-radius: 10px;
									border-bottom-right-radius: 10px;
								}
								
								&.min-availability{
									text-transform: uppercase;
									color: #FF5E5E;
								}
							}
								
							.default .row-content:first-child{
								border-top-left-radius: 0;
							}
						}
					}
					.row-content{
						padding: 10px 16px;
					}
					.color{
						width: 24px;
						height: 24px;
						border-radius: 50%;
						background: gray;
						cursor: pointer;
						transform: rotate(180deg);
						flex-shrink: 0;
						
						&.current{
							outline: 2px solid black;
							cursor: default;
						}
					}
					.stucco{
						width: calc(11% - 10px);
					}
				}
			}
		}
	}
</style>
